import React, { useState, useEffect } from 'react';
import { 
  FiFileText, FiSend, FiEye, FiCheckCircle, FiXCircle, 
  FiClock, FiSearch, FiFilter, FiDownload, FiRefreshCw,
  FiDollarSign, FiUser, FiMail, FiPhone
} from 'react-icons/fi';
import { FaExchangeAlt, FaQrcode, FaPrint, FaBuilding } from 'react-icons/fa';
import './EInvoice.css';

interface Invoice {
  id: number;
  invoice_number: string;
  direction: 'sent' | 'received';
  sender: {
    business_name: string;
    dssn: string;
    email: string;
  };
  recipient: {
    business_name: string;
    dssn: string;
    email: string;
  };
  amount: number;
  currency: 'USD' | 'LRD';
  purpose: string;
  notes: string;
  status: 'pending' | 'paid' | 'cancelled' | 'expired';
  due_date: string;
  paid_at: string;
  created_at: string;
}

interface BusinessLookup {
  dssn: string;
  business_name: string;
  registration_number: string;
  tax_id: string;
  email: string;
  phone: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const EInvoice: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState<Invoice | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDirection, setFilterDirection] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  const [lookupResult, setLookupResult] = useState<BusinessLookup | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });

  const [invoiceForm, setInvoiceForm] = useState({
    recipient_identifier: '',
    amount: 0,
    currency: 'USD' as 'USD' | 'LRD',
    purpose: '',
    notes: '',
    due_date: ''
  });

  const token = localStorage.getItem('merchant_token');

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/merchants/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
        setFilteredInvoices(data.data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showNotification('Failed to load invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Lookup business by DSSN, Tax ID, or Registration Number
  const handleLookupBusiness = async () => {
    if (!invoiceForm.recipient_identifier) {
      showNotification('Please enter DSSN, Tax ID, or Registration Number', 'error');
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const response = await fetch(`${API_URL}/merchants/invoices/lookup-business`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ identifier: invoiceForm.recipient_identifier })
      });

      const data = await response.json();

      if (data.success) {
        setLookupResult(data.data);
        showNotification(`Business found: ${data.data.business_name}`, 'success');
      } else {
        showNotification(data.error || 'Business not found', 'error');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      showNotification('Failed to lookup business', 'error');
    } finally {
      setLookupLoading(false);
    }
  };

  // Send invoice
  const handleSendInvoice = async () => {
    if (!lookupResult) {
      showNotification('Please lookup and verify the recipient business first', 'error');
      return;
    }

    if (invoiceForm.amount <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${API_URL}/merchants/invoices/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_dssn: lookupResult.dssn,
          amount: invoiceForm.amount,
          currency: invoiceForm.currency,
          purpose: invoiceForm.purpose,
          notes: invoiceForm.notes,
          due_date: invoiceForm.due_date || null
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification(data.message, 'success');
        setShowSendModal(false);
        setInvoiceForm({
          recipient_identifier: '',
          amount: 0,
          currency: 'USD',
          purpose: '',
          notes: '',
          due_date: ''
        });
        setLookupResult(null);
        fetchInvoices();
      } else {
        showNotification(data.error || 'Failed to send invoice', 'error');
      }
    } catch (error) {
      console.error('Send invoice error:', error);
      showNotification('Failed to send invoice', 'error');
    } finally {
      setSending(false);
    }
  };

  // Pay invoice
  const handlePayInvoice = async (invoice: Invoice) => {
    if (!window.confirm(`Pay invoice ${invoice.invoice_number} for ${invoice.amount} ${invoice.currency}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/merchants/invoices/${invoice.invoice_number}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        showNotification(data.message, 'success');
        fetchInvoices();
        setShowViewModal(null);
      } else {
        showNotification(data.error || 'Failed to pay invoice', 'error');
      }
    } catch (error) {
      console.error('Pay invoice error:', error);
      showNotification('Failed to pay invoice', 'error');
    }
  };

  // Cancel invoice
  const handleCancelInvoice = async (invoice: Invoice) => {
    if (!window.confirm(`Cancel invoice ${invoice.invoice_number}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/merchants/invoices/${invoice.invoice_number}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason: 'Cancelled by sender' })
      });

      const data = await response.json();

      if (data.success) {
        showNotification(data.message, 'success');
        fetchInvoices();
        setShowViewModal(null);
      } else {
        showNotification(data.error || 'Failed to cancel invoice', 'error');
      }
    } catch (error) {
      console.error('Cancel invoice error:', error);
      showNotification('Failed to cancel invoice', 'error');
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...invoices];

    if (filterStatus !== 'all') {
      filtered = filtered.filter(inv => inv.status === filterStatus);
    }

    if (filterDirection !== 'all') {
      filtered = filtered.filter(inv => inv.direction === filterDirection);
    }

    if (searchTerm) {
      filtered = filtered.filter(inv => 
        inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.sender.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.recipient.business_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  }, [invoices, filterStatus, filterDirection, searchTerm]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'paid':
        return <span className="status-badge paid"><FiCheckCircle /> Paid</span>;
      case 'pending':
        return <span className="status-badge pending"><FiClock /> Pending</span>;
      case 'cancelled':
        return <span className="status-badge cancelled"><FiXCircle /> Cancelled</span>;
      case 'expired':
        return <span className="status-badge expired"><FiXCircle /> Expired</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const summary = {
    total_sent: invoices.filter(i => i.direction === 'sent').length,
    total_received: invoices.filter(i => i.direction === 'received').length,
    total_paid: invoices.filter(i => i.status === 'paid').length,
    total_pending: invoices.filter(i => i.status === 'pending').length,
    total_amount_received: invoices
      .filter(i => i.direction === 'received' && i.status === 'paid')
      .reduce((sum, i) => sum + i.amount, 0),
    total_amount_owed: invoices
      .filter(i => i.direction === 'received' && i.status === 'pending')
      .reduce((sum, i) => sum + i.amount, 0)
  };

  return (
    <div className="einvoice-container">
      {notification.show && (
        <div className={`einvoice-notification ${notification.type}`}>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="einvoice-header">
        <h2>
          <FiFileText /> E-Invoice System
        </h2>
        <button className="send-invoice-btn" onClick={() => setShowSendModal(true)}>
          <FiSend /> Send Invoice
        </button>
      </div>

      <div className="einvoice-summary">
        <div className="summary-card">
          <div className="summary-icon sent">📤</div>
          <div className="summary-info">
            <span>Sent</span>
            <h3>{summary.total_sent}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon received">📥</div>
          <div className="summary-info">
            <span>Received</span>
            <h3>{summary.total_received}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon paid">✅</div>
          <div className="summary-info">
            <span>Paid</span>
            <h3>{summary.total_paid}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon pending">⏳</div>
          <div className="summary-info">
            <span>Pending</span>
            <h3>{summary.total_pending}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon amount">💰</div>
          <div className="summary-info">
            <span>Received Amount</span>
            <h3>{formatCurrency(summary.total_amount_received, 'USD')}</h3>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon owed">💳</div>
          <div className="summary-info">
            <span>Outstanding</span>
            <h3>{formatCurrency(summary.total_amount_owed, 'USD')}</h3>
          </div>
        </div>
      </div>

      <div className="einvoice-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by invoice number or business name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <FiFilter />
          <select value={filterDirection} onChange={(e) => setFilterDirection(e.target.value)}>
            <option value="all">All Directions</option>
            <option value="sent">Sent</option>
            <option value="received">Received</option>
          </select>
        </div>
        <div className="filter-group">
          <FiClock />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button className="refresh-btn" onClick={fetchInvoices} disabled={loading}>
          <FiRefreshCw className={loading ? 'spin' : ''} />
        </button>
      </div>

      <div className="invoices-list">
        {loading ? (
          <div className="loading-invoices">
            <div className="spinner"></div>
            <p>Loading invoices...</p>
          </div>
        ) : filteredInvoices.length > 0 ? (
          filteredInvoices.map((invoice) => (
            <div key={invoice.id} className="invoice-card" onClick={() => setShowViewModal(invoice)}>
              <div className="invoice-header">
                <div className="invoice-number">
                  <FiFileText /> {invoice.invoice_number}
                </div>
                {getStatusBadge(invoice.status)}
              </div>
              <div className="invoice-parties">
                <div className="party">
                  <span className="party-label">From:</span>
                  <strong>{invoice.sender.business_name}</strong>
                </div>
                <div className="party-arrow">→</div>
                <div className="party">
                  <span className="party-label">To:</span>
                  <strong>{invoice.recipient.business_name}</strong>
                </div>
              </div>
              <div className="invoice-details">
                <div className="detail">
                  <span>Amount:</span>
                  <strong className={invoice.direction === 'received' ? 'received-amount' : 'sent-amount'}>
                    {formatCurrency(invoice.amount, invoice.currency)}
                  </strong>
                </div>
                <div className="detail">
                  <span>Purpose:</span>
                  <span>{invoice.purpose || 'N/A'}</span>
                </div>
                <div className="detail">
                  <span>Date:</span>
                  <span>{formatDate(invoice.created_at)}</span>
                </div>
                {invoice.due_date && (
                  <div className="detail">
                    <span>Due Date:</span>
                    <span className={new Date(invoice.due_date) < new Date() && invoice.status === 'pending' ? 'overdue' : ''}>
                      {formatDate(invoice.due_date)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-invoices">
            <FiFileText size={48} />
            <p>No invoices found</p>
            <button className="create-invoice-btn" onClick={() => setShowSendModal(true)}>
              Create Your First Invoice
            </button>
          </div>
        )}
      </div>

      {/* Send Invoice Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => {
          setShowSendModal(false);
          setLookupResult(null);
          setInvoiceForm({
            recipient_identifier: '',
            amount: 0,
            currency: 'USD',
            purpose: '',
            notes: '',
            due_date: ''
          });
        }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h3>Send Invoice</h3>
            
            <div className="form-group">
              <label>Recipient Business (DSSN, Tax ID, or Registration Number)</label>
              <div className="lookup-input">
                <input
                  type="text"
                  value={invoiceForm.recipient_identifier}
                  onChange={(e) => {
                    setInvoiceForm({ ...invoiceForm, recipient_identifier: e.target.value });
                    setLookupResult(null);
                  }}
                  placeholder="Enter DSSN, Tax ID, or Registration Number"
                />
                <button onClick={handleLookupBusiness} disabled={lookupLoading} className="lookup-btn">
                  {lookupLoading ? '...' : <FiSearch />} Verify
                </button>
              </div>
            </div>

            {lookupResult && (
              <div className="lookup-result business">
                <div className="lookup-header">
                  <FaBuilding /> Business Found
                </div>
                <div className="lookup-details">
                  <p><strong>{lookupResult.business_name}</strong></p>
                  <p><FiMail /> {lookupResult.email}</p>
                  <p><FiPhone /> {lookupResult.phone}</p>
                  <p><FaExchangeAlt /> DSSN: {lookupResult.dssn}</p>
                  <p>Reg No: {lookupResult.registration_number}</p>
                  <p>Tax ID: {lookupResult.tax_id}</p>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Amount</label>
                <div className="amount-input">
                  <select 
                    value={invoiceForm.currency}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, currency: e.target.value as 'USD' | 'LRD' })}
                  >
                    <option value="USD">USD</option>
                    <option value="LRD">LRD</option>
                  </select>
                  <input
                    type="number"
                    value={invoiceForm.amount || ''}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Due Date (Optional)</label>
                <input
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Purpose / Service</label>
              <input
                type="text"
                value={invoiceForm.purpose}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, purpose: e.target.value })}
                placeholder="e.g., Consulting Services, Product Purchase"
              />
            </div>

            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                placeholder="Additional notes for the recipient..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => {
                setShowSendModal(false);
                setLookupResult(null);
              }}>Cancel</button>
              <button 
                className="send-btn" 
                onClick={handleSendInvoice} 
                disabled={sending || !lookupResult}
              >
                {sending ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && (
        <div className="modal-overlay" onClick={() => setShowViewModal(null)}>
          <div className="modal-content invoice-detail" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-detail-header">
              <h3>Invoice Details</h3>
              {getStatusBadge(showViewModal.status)}
            </div>

            <div className="invoice-detail-number">
              <FiFileText /> {showViewModal.invoice_number}
            </div>

            <div className="invoice-parties-detail">
              <div className="party-detail sender">
                <h4>From (Sender)</h4>
                <p><strong>{showViewModal.sender.business_name}</strong></p>
                <p><FiMail /> {showViewModal.sender.email}</p>
                <p><FaExchangeAlt /> DSSN: {showViewModal.sender.dssn}</p>
              </div>
              <div className="party-arrow-large">→</div>
              <div className="party-detail recipient">
                <h4>To (Recipient)</h4>
                <p><strong>{showViewModal.recipient.business_name}</strong></p>
                <p><FiMail /> {showViewModal.recipient.email}</p>
                <p><FaExchangeAlt /> DSSN: {showViewModal.recipient.dssn}</p>
              </div>
            </div>

            <div className="invoice-detail-info">
              <div className="info-row">
                <span>Amount:</span>
                <strong className={showViewModal.direction === 'received' ? 'received-amount' : 'sent-amount'}>
                  {formatCurrency(showViewModal.amount, showViewModal.currency)}
                </strong>
              </div>
              <div className="info-row">
                <span>Purpose:</span>
                <span>{showViewModal.purpose || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span>Notes:</span>
                <span>{showViewModal.notes || 'No notes'}</span>
              </div>
              <div className="info-row">
                <span>Created:</span>
                <span>{formatDate(showViewModal.created_at)}</span>
              </div>
              {showViewModal.due_date && (
                <div className="info-row">
                  <span>Due Date:</span>
                  <span className={new Date(showViewModal.due_date) < new Date() && showViewModal.status === 'pending' ? 'overdue' : ''}>
                    {formatDate(showViewModal.due_date)}
                  </span>
                </div>
              )}
              {showViewModal.paid_at && (
                <div className="info-row">
                  <span>Paid On:</span>
                  <span>{formatDate(showViewModal.paid_at)}</span>
                </div>
              )}
            </div>

            <div className="modal-actions">
              {showViewModal.direction === 'received' && showViewModal.status === 'pending' && (
                <button className="pay-btn" onClick={() => handlePayInvoice(showViewModal)}>
                  <FiDollarSign /> Pay Invoice
                </button>
              )}
              {showViewModal.direction === 'sent' && showViewModal.status === 'pending' && (
                <button className="cancel-invoice-btn" onClick={() => handleCancelInvoice(showViewModal)}>
                  <FiXCircle /> Cancel Invoice
                </button>
              )}
              <button className="close-btn" onClick={() => setShowViewModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EInvoice;
